import { Dropdown, Avatar, Button, Space, message } from 'antd';
import { UserOutlined, LogoutOutlined, DashboardOutlined, BankOutlined, ClockCircleOutlined, CheckCircleOutlined, CheckCircleFilled, CloseCircleOutlined, AppstoreOutlined, GlobalOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import LoginModal from '../LoginModal';
import RegisterModal from '../RegisterModal';
import s from './index.module.css';
import { SUPERVISION_LOGIN_URL } from '../../config/constants';
import { readToken, readUserInfo, clearUserInfoStorage, refreshUserInfoCache, emitUserInfoUpdated } from '../../utils/userInfo';
import { ADMIN_PATH_PREFIX } from '../../config/userMenu';

const UserDropdown = ({ adminMode = false }) => {
  const [userInfo, setUserInfo] = useState(readUserInfo);
  const [authReady, setAuthReady] = useState(() => {
    const cachedUserInfo = readUserInfo();
    return !readToken() || Boolean(cachedUserInfo?.id);
  });
  const [loginVisible, setLoginVisible] = useState('');
  const [registerVisible, setRegisterVisible] = useState('');
  const history = useHistory();
  const location = useLocation();

  // 从 URL 查询参数自动打开登录弹窗（如密码重置成功后跳转 /?openLogin=organization）
  useEffect(() => {
    const loginParam = new URLSearchParams(location.search).get('openLogin');
    if (loginParam === 'personal' || loginParam === 'organization') {
      setLoginVisible(loginParam);
    }
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;

    const syncUserInfo = async (force = false) => {
      const currentUserInfo = readUserInfo();
      const token = readToken();

      if (!token) {
        if (!cancelled) {
          setUserInfo(null);
          setAuthReady(true);
        }
        return;
      }

      if (!cancelled && currentUserInfo?.id) {
        setUserInfo((prev) => prev || currentUserInfo);
        setAuthReady(true);
      }

      const nextUserInfo = await refreshUserInfoCache({ force });

      if (!cancelled) {
        setUserInfo(nextUserInfo || currentUserInfo);
        setAuthReady(true);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncUserInfo();
      }
    };

    syncUserInfo();
    window.addEventListener('focus', () => syncUserInfo());
    document.addEventListener('visibilitychange', handleVisibilityChange);
    const refreshTimer = window.setInterval(() => {
      if (readToken()) {
        syncUserInfo();
      }
    }, 30000);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', syncUserInfo);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(refreshTimer);
    };
  }, []);

  const getApprovalStatus = (targetUserInfo = userInfo) => {
    if (!targetUserInfo || targetUserInfo.userType !== 2) {
      return null;
    }

    if (Number(targetUserInfo?.state) === 1) {
      return {
        color: 'success',
        text: '已通过',
        icon: <CheckCircleOutlined />,
        messageType: 'success',
        passed: true,
      };
    }

    if (Number(targetUserInfo?.state) === 2) {
      return {
        color: 'processing',
        text: '审批中',
        icon: <ClockCircleOutlined />,
        messageType: 'info',
        passed: false,
      };
    }

    if (Number(targetUserInfo?.state) === 3) {
      return {
        color: 'error',
        text: '已注销',
        icon: <CloseCircleOutlined />,
        messageType: 'warning',
        passed: false,
      };
    }

    if (Number(targetUserInfo?.state) === 4) {
      return {
        color: 'error',
        text: '待补充',
        icon: <CloseCircleOutlined />,
        messageType: 'warning',
        passed: false,
      };
    }

    // const registerLogList = targetUserInfo.registerLogList;
    // if (!registerLogList || registerLogList.length === 0) {
    //   return {
    //     color: 'processing',
    //     text: '审批中',
    //     icon: <ClockCircleOutlined />,
    //     messageType: 'info',
    //     passed: false,
    //   };
    // }
    //
    // const sortedLogs = [...registerLogList].sort((a, b) =>
    //   new Date(b.createTime) - new Date(a.createTime)
    // );
    // const latestLog = sortedLogs[0];
    //
    // const statusMap = {
    //   1: {
    //     color: 'processing',
    //     text: '审批中',
    //     icon: <ClockCircleOutlined />,
    //     messageType: 'info',
    //     passed: false,
    //   },
    //   2: {
    //     color: 'success',
    //     text: '已通过',
    //     icon: <CheckCircleOutlined />,
    //     messageType: 'success',
    //     passed: true,
    //   },
    //   3: {
    //     color: 'error',
    //     text: '需补充',
    //     icon: <CloseCircleOutlined />,
    //     messageType: 'warning',
    //     passed: false,
    //   },
    // };
    //
    // return statusMap[latestLog.regState] || null;
  };

  const handleLogin = (userData) => {
    setUserInfo(userData);
    setAuthReady(true);
    setLoginVisible('');
    emitUserInfoUpdated();
    // message.success(`欢迎回来，${userData?.userName}！`);

    const approvalStatus = getApprovalStatus(userData);

    if (!adminMode) {
      // 登录成功后统一进入管理中心
      if (approvalStatus && !approvalStatus.passed) {
        setTimeout(() => {
          message[approvalStatus.messageType]({
            content: `您的机构申请${approvalStatus.text}，请前往账号设置查看详情`,
            duration: 5,
            key: 'approval_status',
          });
        }, 1000);

        history.push(`${ADMIN_PATH_PREFIX}/account-settings`);
      } else {
        history.push(ADMIN_PATH_PREFIX);
      }
    }

    // 刷新页面
    // window.location.reload();
  };

  const handleRegister = (userData) => {
    setRegisterVisible(false);
    message.success('注册申请已提交，请等待审核');
  };

  const handleSwitchToRegister = (type) => {
    setLoginVisible('');
    setRegisterVisible(type);
  };

  const handleSwitchToLogin = (type) => {
    setRegisterVisible('');
    setLoginVisible(type);
  };

  const handleLogout = () => {
      sessionStorage.removeItem('token');
      clearUserInfoStorage();
      setUserInfo(null);
      setAuthReady(true);
      message.success('已退出登录');

      // 检查当前是否在首页
      const isHomePage = location.pathname === '/' || location.pathname === '';

      if (isHomePage) {
        // 如果在首页，刷新页面
        window.location.reload();
      } else {
        // 如果不在首页，跳转到首页
        history.push('/');
      }
  };

  const handleSupervisionLogin = () => {
    window.open(SUPERVISION_LOGIN_URL, '_blank');
  };

  const approvalStatus = getApprovalStatus();
  const shouldRedirectToAccountSettings = Boolean(approvalStatus && !approvalStatus.passed);

  const userMenuItems = adminMode
    ? [
        {
          key: 'logout',
          icon: <LogoutOutlined />,
          label: '退出登录',
          onClick: handleLogout
        }
      ]
    : [
        {
          key: 'logout',
          icon: <LogoutOutlined />,
          label: '退出登录',
          onClick: handleLogout
        }
      ];

  if (!authReady) {
    return (
      <>
        <div className={s.userSkeleton} aria-hidden="true" />

        <LoginModal
          visible={loginVisible}
          onCancel={() => setLoginVisible('')}
          onLoginSuccess={handleLogin}
          onSwitchToRegister={handleSwitchToRegister}
        />

        <RegisterModal
          visible={registerVisible}
          onCancel={() => setRegisterVisible('')}
          onRegisterSuccess={handleRegister}
          onSwitchToLogin={handleSwitchToLogin}
        />
      </>
    );
  }

  if (!userInfo) {
    // 未登录状态
    return (
      <>
        <Space className={s.userActions}>
          <Button
            type="text"
            icon={<UserOutlined />}
            onClick={() => setLoginVisible('personal')}
            className={s.authButton}
          >
            个人登录
          </Button>
          <Button
            type="text"
            icon={<BankOutlined />}
            onClick={() => setLoginVisible('organization')}
            className={s.authButton}
          >
            机构登录
          </Button>
          <Button
            type="primary"
            icon={<DashboardOutlined />}
            onClick={handleSupervisionLogin}
            className={s.authButton}
          >
            监管登录
          </Button>
        </Space>

        <LoginModal
          visible={loginVisible}
          onCancel={() => setLoginVisible('')}
          onLoginSuccess={handleLogin}
          onSwitchToRegister={handleSwitchToRegister}
        />

        <RegisterModal
          visible={registerVisible}
          onCancel={() => setRegisterVisible('')}
          onRegisterSuccess={handleRegister}
          onSwitchToLogin={handleSwitchToLogin}
        />
      </>
    );
  }

  const userInfoNode = (
    <div
      className={s.userInfo}
      onClick={shouldRedirectToAccountSettings ? () => history.push('/account-settings') : undefined}
    >
      <Avatar
        size={32}
        icon={<UserOutlined />}
        className={s.userAvatar}
      />
      <div className={s.userMeta}>
        <div className={s.nameRow}>
          <span className={s.username}>{userInfo.userName}</span>
          {approvalStatus?.passed && (
            <span className={s.verifiedBadge}>
              <CheckCircleFilled />
              已认证
            </span>
          )}
          {approvalStatus && !approvalStatus.passed && (
            <span className={`${s.statusChip} ${approvalStatus.color === 'error' ? s.statusError : s.statusPending}`}>
              {approvalStatus.icon}
              {approvalStatus.text}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  // 已登录状态
  return (
    <>
      <Space className={s.userActions}>
        {!adminMode && (
          <Button
            type="text"
            // icon={<AppstoreOutlined />}
            onClick={() => history.push(ADMIN_PATH_PREFIX)}
            className={s.adminButton}
          >
            管理中心
          </Button>
        )}
        {adminMode && (
          <Button
            type="text"
            // icon={<GlobalOutlined />}
            onClick={() => history.push('/')}
            className={s.adminButton}
          >
            返回网站
          </Button>
        )}
        <Dropdown
          menu={{ items: userMenuItems }}
          placement="bottomRight"
          trigger={['click']}
          className={s.userDropdown}
        >
          {userInfoNode}
        </Dropdown>
      </Space>

      <LoginModal
        visible={loginVisible}
        onCancel={() => setLoginVisible('')}
        onLoginSuccess={handleLogin}
        onSwitchToRegister={handleSwitchToRegister}
      />

      <RegisterModal
        visible={registerVisible}
        onCancel={() => setRegisterVisible('')}
        onRegisterSuccess={handleRegister}
        onSwitchToLogin={handleSwitchToLogin}
      />
    </>
  );
};

export default UserDropdown;
